import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import Link from 'next/link';
import track from '@helper/tracking';
import AnchorLinks from '../../molecules/anchor-links/anchorLinks';

import Breadcrumb from '../../molecules/breadcrumb/breadcrumb';
import Button from '../../atoms/button/button';
import Container from '../../layout/container/container';
import Copy from '../../atoms/copy/copy';
import Date from '../../atoms/date/date';
import FilterTags from '../../atoms/filter-tags/filterTags';
import FullscreenMedia from '../../molecules/fullscreen-media/fullscreenMedia';
import Headline from '../../atoms/headline/headline';
import LongArrowRight from '../../icons/longArrowRight.svg';
import MixedCopy from '../../molecules/mixed-copy/mixedCopy';
import StyledStage from './stage.style';

import CuratedExhibitionTeasers from '../curated-exhibition-teasers/curatedExhibitionTeasers';
import CuratedExhibitionSubHeadline from '../curated-exhibition-sub-headline/curatedExhibitionSubHeadline';

const Image = dynamic(() => import('../../atoms/image/image'));
const EventInfo = dynamic(() => import('../../molecules/event-info/eventInfo.withRedux'));

const AudioPLayer = dynamic({
  loader: () => import('../../molecules/audio-player/audioPlayer'),
  ssr: false
});

const Stage = ({
  anchorLinks,
  audioPlayerReadOut,
  breadcrumb,
  curatedExhibitionSubheadline,
  curatedExhibitionTeasers,
  curatedIntroVideo,
  eventPrices,
  eventType,
  headline,
  introCopy,
  introVideo,
  pageTags,
  postDate,
  shopText,
  shopUrl,
  showDate = false,
  stageLink,
  subline,
  teaserImage,
  titleImage,
  ...props
}) => {
  const sizes = [{ condition: 'min-width: 940px', width: '50vw' }, { width: '80vw' }];
  const image = titleImage || teaserImage;

  return (
    <StyledStage hasImage={titleImage} imageFormat={image ? image.imageFormat : null} {...props}>
      {breadcrumb && <Breadcrumb entries={breadcrumb} />}
      <Container className="stage__container">
        {curatedIntroVideo && curatedIntroVideo.type === 'embeddedVideo' && (
          <FullscreenMedia
            alignment="right"
            customTheme="#D4EAF3"
            type="introVideo"
            hoster={curatedIntroVideo.hoster}
            videoId={curatedIntroVideo.videoId}
            previewImage={teaserImage}
          />
        )}

        {teaserImage && !curatedIntroVideo && !titleImage && (
          <FullscreenMedia
            type="fullwidthImage"
            alignment="right"
            customTheme="#D4EAF3"
            file={teaserImage}
          />
        )}

        <div className="stage__wrapper">
          {titleImage && (
            <>
              <div className="stage__image--mobile">
                <Image {...titleImage} lazy="default" sizes={sizes} />
              </div>
              {titleImage && titleImage.credits && (
                <div
                  className="stage__credits stage__credits--mobile"
                  dangerouslySetInnerHTML={{
                    __html: titleImage.credits
                  }}
                />
              )}
            </>
          )}

          {pageTags && pageTags.length > 0 && (
            <FilterTags tags={pageTags} className="stage__categories" />
          )}

          {audioPlayerReadOut && (
            <AudioPLayer
              file={audioPlayerReadOut}
              alignment="none"
              progressInitiallyVisible={false}
              compact
            />
          )}

          {subline && (
            <Headline tag="h3" subline>
              {subline}
            </Headline>
          )}

          <Headline tag="h1" primary>
            {headline}
          </Headline>

          {postDate && showDate && (
            <Date className="stage__date" month={postDate.m} day={postDate.d} year={postDate.Y} />
          )}

          {introCopy && (
            <MixedCopy focused fullWidth html={introCopy} className="stage__introduction" />
          )}

          {stageLink && (
            <Link
              href={{
                pathname: '/universal',
                query: { uri: stageLink.uri }
              }}
              as={stageLink.uri}
              className="stage__link"
              title={stageLink.label}
            >
              <LongArrowRight width="60px" height="20px" />
              <Copy thick noMargin>
                {stageLink.label}
              </Copy>
            </Link>
          )}

          {shopUrl && (
            <Link
              href={shopUrl}
              title={shopText}
              rel="noopener noreferrer"
              target={shopUrl.includes('tickets.futurium.') ? undefined : '_blank'}
              data-gomus-embed={shopUrl.includes('tickets.futurium.') ? true : undefined}
            >
              <Button
                onClick={() => {
                  track(['trackEvent', 'Booking', 'Click', shopText]);
                }}
              >
                {shopText}
              </Button>
            </Link>
          )}
        </div>
      </Container>
      <Container>
        {curatedExhibitionTeasers && (
          <CuratedExhibitionTeasers {...curatedExhibitionTeasers} noMargin />
        )}

        {curatedExhibitionSubheadline && (
          <CuratedExhibitionSubHeadline headline={curatedExhibitionSubheadline} />
        )}

        {anchorLinks && <AnchorLinks links={anchorLinks} />}
        {eventType && (
          <div className="stage__event-wrapper">
            <EventInfo />
          </div>
        )}
      </Container>
      {titleImage && (
        <div className="stage__image--desktop">
          <Image {...titleImage} lazy="default" sizes={sizes} />

          {titleImage.credits && (
            <div
              className="stage__credits"
              dangerouslySetInnerHTML={{ __html: titleImage.credits }}
            />
          )}
        </div>
      )}
    </StyledStage>
  );
};

Stage.propTypes = {
  showDate: PropTypes.bool,
  shopUrl: PropTypes.string,
  shopText: PropTypes.string.isRequired,
  pageTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      slug: PropTypes.string,
      status: PropTypes.string
    })
  ),
  headline: PropTypes.string,
  subline: PropTypes.string,
  postDate: PropTypes.shape({
    c: PropTypes.string,
    Y: PropTypes.string,
    m: PropTypes.string,
    d: PropTypes.string,
    H: PropTypes.string,
    i: PropTypes.string
  }),
  introCopy: PropTypes.string,
  titleImage: PropTypes.shape({
    imageFormat: PropTypes.oneOf(['portrait', 'landscape']),
    credits: PropTypes.string
  }),
  breadcrumb: PropTypes.arrayOf(PropTypes.shape()),
  eventPrices: PropTypes.arrayOf(PropTypes.shape()),
  eventType: PropTypes.arrayOf(PropTypes.shape()),
  anchorLinks: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  stageLink: PropTypes.shape({
    uri: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  introVideo: PropTypes.shape({
    src: PropTypes.string.isRequired,
    title: PropTypes.string
  }),
  curatedIntroVideo: PropTypes.shape({
    type: PropTypes.string,
    hoster: PropTypes.string,
    videoId: PropTypes.string
  }),
  teaserImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    credits: PropTypes.string,
    imageFormat: PropTypes.oneOf(['portrait', 'landscape'])
  }),
  curatedExhibitionTeasers: PropTypes.shape(),
  curatedExhibitionSubheadline: PropTypes.string,

  // TODO @Thomas: This needs rework, maybe
  audioPlayerReadOut: PropTypes.shape({
    mimeType: PropTypes.string,
    src: PropTypes.string.isRequired
  })
};

const mapStateToProps = state => ({
  pageTags: state.app.pageData.pageTags,
  headline: state.app.pageData.headline,
  subline: state.app.pageData.subline,
  postDate: state.app.pageData.postDate,
  introCopy: state.app.pageData.introCopy,
  titleImage: state.app.pageData.titleImage,
  breadcrumb: state.app.pageData.breadcrumb,
  eventType: state.app.pageData.eventType,
  eventPrices: state.app.pageData.eventPrices,
  anchorLinks: state.app.pageData.anchorLinks,
  stageLink: state.app.pageData.stageLink,
  introVideo: state.app.pageData.introVideo,
  curatedIntroVideo: state.app.pageData.curatedIntroVideo,
  teaserImage: state.app.pageData.teaserImage,
  audioPlayerReadOut: state.app.pageData.audioPlayerReadOut
});

export { Stage as StageComponent };
export default connect(mapStateToProps)(Stage);
